import type { FC, ForwardedRef, InputHTMLAttributes, ReactNode, MouseEventHandler } from "react";
import { forwardRef, useEffect, useState } from "react";
import { cn } from "@akina/utils";
import { clsx } from "clsx";
import { Icon } from "./Icon/Icon";
import { SpacingY } from "./Spacing";
import { Text } from "./Text";

export type Size = "5xl" | "4xl" | "3xl" | "2xl" | "xl" | "lg" | "base" | "sm" | "xs";

export type InputProps = {
  fontSize?: Size;
  error?: string;
  label?: string;
  labelPosition?: "top" | "left";
  icon?: {
    position?: "left" | "right";
    icon: ReturnType<typeof Icon>;
  };
  noOutline?: boolean;
  wrapperClassName?: string;
  showValueSelected?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

export const getClassesBySize = (size: Size): string => {
  switch (size) {
    case "5xl": {
      return "text-5xl";
    }
    case "4xl": {
      return "text-4xl";
    }
    case "3xl": {
      return "text-3xl";
    }
    case "2xl": {
      return "text-2xl";
    }
    case "lg": {
      return "text-lg";
    }
    case "base": {
      return "text-base";
    }
    case "sm": {
      return "text-sm";
    }
    case "xs": {
      return "text-xs";
    }
    default: {
      return "";
    }
  }
};

export const DEFAULT_FONT_SIZE: Size = "base";

const baseInputClasses =
  "flex font-area appearance-none h-12 border bg-white border-gray-800/15 hover:border-primary-100 focus-within:border-primary-100 active:border-primary-100 rounded-full w-full py-2 px-4 text-gray-800 leading-tight focus:outline-none";

const errorInputClasses = "border-error-100 shadow-sm";

const outlineInputClasses =
  "h-12 ring-primary-100 transition-all duration-75 focus-within:ring-1 hover:ring-1 focus:ring-1";

const getInputContainerClasses = (
  fontSize: Size,
  className: string | undefined,
  error: string | undefined,
  noOutline: boolean | undefined,
  disabled: boolean | undefined,
): string =>
  cn(
    baseInputClasses,
    getClassesBySize(fontSize),
    className ?? "",
    error ? errorInputClasses : "",
    noOutline ? "" : outlineInputClasses,
    disabled && "bg-gray-800/2 border-gray-800/10 text-gray-800/30",
  );

export const Label: FC<{ label: string; id?: string; disabled?: boolean }> = ({ label, id, disabled }) => (
  <div>
    <label htmlFor={id} className={cn("cursor-pointer", disabled && "cursor-not-allowed")}>
      <Text color={disabled === true ? "text-gray-800/50" : "text-gray-800"}>{label}</Text>
    </label>
    <SpacingY level={2} />
  </div>
);

export const Input = forwardRef(
  (
    {
      className,
      fontSize = DEFAULT_FONT_SIZE,
      error,
      label,
      icon,
      noOutline,
      labelPosition = "left",
      wrapperClassName,
      showValueSelected,
      ...props
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => (
    <div className={cn(wrapperClassName, labelPosition === "top" && "flex flex-col")}>
      {label ? <Label label={label} id={props.id} disabled={props.disabled} /> : null}

      <div className={getInputContainerClasses(fontSize, className, error, noOutline, props.disabled)}>
        {icon && icon?.position === "left" ? <div className="mr-3 flex items-center">{icon.icon}</div> : null}

        <input
          ref={ref}
          {...props}
          className={clsx(
            "h-full w-full appearance-none bg-transparent placeholder:text-gray-800/50 focus:outline-none",
            props.disabled && "cursor-not-allowed placeholder:text-gray-800/30",
          )}
        />

        {icon && icon?.position === "right" ? (
          <div className={clsx("ml-3 flex items-center gap-1", props.disabled && "cursor-not-allowed")}>
            {showValueSelected ? <SomeValueSelectedDot /> : null}
            {icon.icon}
          </div>
        ) : null}
      </div>
    </div>
  ),
);
Input.displayName = "Input";

type InputIconProps = {
  onClick?: MouseEventHandler<HTMLButtonElement>;
} & Omit<InputProps, "label" | "labelPosition" | "onClick">;

export const InputIcon = forwardRef(
  (
    {
      className,
      fontSize = DEFAULT_FONT_SIZE,
      error,
      icon,
      noOutline,
      wrapperClassName,
      showValueSelected,
      onClick,
      "aria-pressed": ariaPressed,
      ...props
    }: InputIconProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    return (
      <button
        type="button"
        disabled={props.disabled}
        className={cn(wrapperClassName, props.disabled ? "cursor-not-allowed" : "cursor-pointer")}
        onClick={onClick}
        aria-pressed={ariaPressed}>
        <div className={getInputContainerClasses(fontSize, className, error, noOutline, props.disabled)}>
          {icon && icon?.position === "left" ? <div className="mr-3 flex items-center">{icon.icon}</div> : null}

          <input ref={ref} {...props} className={clsx("hidden")} />

          {icon && icon?.position === "right" ? (
            <div className={clsx("ml-3 flex items-center gap-1", props.disabled && "cursor-not-allowed")}>
              {showValueSelected ? <SomeValueSelectedDot /> : null}
              {icon.icon}
            </div>
          ) : null}
        </div>
      </button>
    );
  },
);
InputIcon.displayName = "InputIcon";

export const EmailInput: FC<InputProps> = ({ ...props }) => <Input {...{ ...props, type: "email" }} />;

export const PasswordInput = ({
  error,
  fontSize = DEFAULT_FONT_SIZE,
  className,
  label,
  icon,
  ...props
}: InputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = () => {
    setShowPassword(prevShowPassword => !prevShowPassword);
  };

  return (
    <>
      {label ? <Label label={label} id={props.id} disabled={props.disabled} /> : null}

      <div
        className={cn(baseInputClasses, getClassesBySize(fontSize), className ?? "", "flex", error ? errorInputClasses : "")}>
        {icon && icon?.position === "left" ? <div>{icon.icon}</div> : null}

        <input
          {...props}
          type={showPassword ? "text" : "password"}
          className={"h-full w-full appearance-none focus:outline-none"}
        />
        <button onClick={toggleShowPassword}>{showPassword ? <Icon type="eyeOn" /> : <Icon type="eyeOff" />}</button>

        {icon && icon?.position === "right" ? <div>{icon.icon}</div> : null}
      </div>
    </>
  );
};

export type DateInputProps = {
  datePicker?: ReactNode;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & { "data-testid"?: string };
} & InputProps;

export const DateInput = forwardRef(
  (
    {
      error,
      fontSize = DEFAULT_FONT_SIZE,
      className,
      datePicker,
      placeholder,
      label,
      icon,
      inputProps,
      ...props
    }: DateInputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => (
    <div className="w-full flex-col">
      {label ? <Label label={label} id={props.id} disabled={props.disabled} /> : null}
      <div
        className={cn(baseInputClasses, getClassesBySize(fontSize), className ?? "", "flex items-center", outlineInputClasses, error ? errorInputClasses : "")}>
        {icon && icon?.position === "left" ? <div>{icon.icon}</div> : null}
        <input
          {...props}
          type={"text"}
          placeholder={placeholder}
          ref={ref}
          className={clsx(
            "h-full w-full appearance-none bg-transparent placeholder:text-gray-800/50 focus:outline-none",
            props.disabled && "cursor-not-allowed",
          )}
          {...inputProps}
        />
        {datePicker}
        {icon && icon?.position === "right" ? <div>{icon.icon}</div> : null}
      </div>
    </div >
  ),
);

DateInput.displayName = "DateInput";

type DebouncedInputProps = {
  value: string;
  debounce?: number;
  onChange: (value: string) => void;
} & Omit<InputProps, "onChange">;

export const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: DebouncedInputProps & Omit<InputHTMLAttributes<HTMLInputElement>, "onChange">) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return <Input {...props} value={value} onChange={e => setValue(e.target.value)} />;
};

const SomeValueSelectedDot = () => <div className="bg-primary-100 h-2 w-2 rounded-full" />;
